import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card as CardData, CardType, GamePhase, GameState, ActivationAttempt } from '../types'
import { getEffectiveEssenceCostForPlayer } from '../services/gameLogic'
import { getCardDisplayNameForCard, getTypeIconForType, getCardSummaryForType } from '../constants'
import { Language, t } from '../data/translations'
import { useGameSettings } from './useGameSettings'

export const useHints = (
  params: {
    gameState: GameState | null,
    localPlayerId: number,
    playerIsReady: boolean,
    currentActivation: ActivationAttempt | null,
    language: Language,
    isPaused?: boolean
  }
) => {
  const { gameState, localPlayerId, playerIsReady, currentActivation, language, isPaused } = params
  const { settings } = useGameSettings()
  const tooltipsEnabled = settings.tooltipsEnabled

  const [hintVisible, setHintVisible] = useState(false)
  const [hintTitle, setHintTitle] = useState('')
  const [hintLines, setHintLines] = useState<string[]>([])
  const [hintIcons, setHintIcons] = useState<React.ReactNode[]>([])
  const [idleHintVisible, setIdleHintVisible] = useState(false)
  const [idleHintTitle, setIdleHintTitle] = useState('')
  const [idleHintLines, setIdleHintLines] = useState<string[]>([])
  const [isDismissed, setIsDismissed] = useState(false)
  const [idleHintDismissCount, setIdleHintDismissCount] = useState(() => {
    return parseInt(localStorage.getItem('idleHintDismissCount') || '0', 10)
  })
  const [lastActionAt, setLastActionAt] = useState<number>(Date.now())
  const [hoverCardInfo, setHoverCardInfo] = useState<{ card: CardData; rotation: number; source: string } | null>(null)
  const hoverTimerRef = useRef<number | null>(null)
  const reactTimerRef = useRef<number | null>(null)
  // Progressive hint masking
  const hintShownAtRef = useRef<number>(0)
  const [hintPenalty, setHintPenalty] = useState<number>(() => {
    return parseInt(localStorage.getItem('hintPenaltyScore') || '0', 10)
  })
  const maskingDelay = Math.min(hintPenalty * 2500, 25000) // max 25 seconds masking

  const markAction = useCallback(() => {
    console.log('[useHints][markAction]', {
      phase: gameState?.phase ?? null,
      round: gameState?.round ?? null,
      turnPlayerId: gameState?.turnPlayerId ?? null,
      activationsLength: gameState?.activations?.length ?? 0,
    })
    setLastActionAt(Date.now())
    setIdleHintVisible(false)
    setIsDismissed(false)
  }, [gameState?.phase, gameState?.round, gameState?.turnPlayerId, gameState?.activations?.length])

  const handleCloseReactionHint = useCallback(() => {
    const timeShown = Date.now() - hintShownAtRef.current
    if (timeShown < 2500) {
      // User dismissed too fast — increase penalty
      setHintPenalty(prev => {
        const next = Math.min(prev + 1, 10)
        localStorage.setItem('hintPenaltyScore', next.toString())
        return next
      })
    } else {
      // User kept it open — slowly decay penalty
      setHintPenalty(prev => {
        const next = Math.max(0, prev - 1)
        localStorage.setItem('hintPenaltyScore', next.toString())
        return next
      })
    }
    setHintVisible(false)
  }, [])

  const dismissIdleHint = useCallback(() => {
    setIsDismissed(true)
    setIdleHintVisible(false)
    setLastActionAt(Date.now())
    const newCount = idleHintDismissCount + 1
    setIdleHintDismissCount(newCount)
    localStorage.setItem('idleHintDismissCount', newCount.toString())
  }, [idleHintDismissCount])

  const handleCardMouseEnterApp = useCallback((card: CardData, rotation: number, _source: string) => {
    setHoverCardInfo({ card, rotation, source: _source })
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = window.setTimeout(() => {
      const finalRotation = (rotation % 360 + 360) % 360
      const flipped = finalRotation > 90 && finalRotation < 270
      const type = flipped && card.typeB ? card.typeB : card.type
      const lines: string[] = []
      const special: string[] = []
      if (card.essenceCost) {
        const me = gameState?.players.find(p => p.id === localPlayerId)
        const owner = gameState?.players.find(p => p.id === card.ownerId) || me
        const showDiscount = !!owner && owner.playerClass === 'OCCULTIST' && card.subType === 'Artifact'
        const costToShow = showDiscount ? getEffectiveEssenceCostForPlayer(card, owner!) : card.essenceCost
        special.push(`${t('term.essence', language)} ${costToShow}`)
      }
      const shieldTypes: CardType[] = [CardType.Hibernation, CardType.Dispel, CardType.Pushback, CardType.Defense, CardType.PsyBlock, CardType.Frost, CardType.Invincible]
      if (shieldTypes.includes(type) || card.extendedShield) special.push(t('term.shieldReaction', language))
      if (card.playableAfterPass) special.push(t('term.playableAfterPass', language))
      lines.push(getCardSummaryForType(type, (card as any).faceSlug, language))
      if (special.length > 0) lines.push(special.join(' • '))
      setHintTitle(getCardDisplayNameForCard(card, rotation, language))
      setHintLines(lines)
      setHintIcons([getTypeIconForType(type)])
      if (gameState?.phase === GamePhase.StashChoice) return
      if (!tooltipsEnabled) return
      if (isPaused) return
      hintShownAtRef.current = Date.now()
      setHintVisible(true)
    }, 1200)
  }, [gameState, localPlayerId, language, tooltipsEnabled, isPaused])

  const handleCardMouseLeaveApp = useCallback(() => {
    setHoverCardInfo(null)
    if (hoverTimerRef.current) { window.clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null }
    setHintVisible(false)
  }, [])

  useEffect(() => {
    markAction()
  }, [gameState, markAction])

  useEffect(() => {
    console.log('[useHints][state]', {
      phase: gameState?.phase ?? null,
      round: gameState?.round ?? null,
      turnPlayerId: gameState?.turnPlayerId ?? null,
      activationsLength: gameState?.activations?.length ?? 0,
      idleHintVisible,
      isPaused: !!isPaused,
      lastActionAt,
      isDismissed,
    })
  }, [gameState?.phase, gameState?.round, gameState?.turnPlayerId, gameState?.activations?.length, idleHintVisible, isPaused, lastActionAt, isDismissed])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (!gameState || isPaused || idleHintDismissCount >= 2) {
        if (idleHintVisible) setIdleHintVisible(false)
        return
      }
      const now = Date.now()
      let threshold: number | null = null
      if (gameState.phase === GamePhase.Preparation && !playerIsReady) {
        threshold = 30000
      } else if (
        [GamePhase.Combat, GamePhase.TowerPurchase, GamePhase.AWAITING_PURIFICATION_TARGET].includes(gameState.phase) &&
        gameState.turnPlayerId === localPlayerId &&
        !currentActivation
      ) {
        threshold = isDismissed ? 30000 : 15000
      } else if (
        gameState.phase === GamePhase.Combat &&
        currentActivation &&
        gameState.lastCardPlayerId !== undefined &&
        gameState.lastCardPlayerId !== localPlayerId
      ) {
        // Player's turn to respond in an active chain
        threshold = isDismissed ? 30000 : 15000
      }

      if (threshold !== null) {
        if (now - lastActionAt >= threshold) {
          if (gameState.phase === GamePhase.Preparation) {
            setIdleHintTitle(t('ui.idleHintDayTitle', language))
            const rawLines = [
              t('ui.idleHintDayLine1', language),
              t('ui.idleHintDayLine2', language),
              t('ui.idleHintDayLine3', language),
              t('ui.idleHintDayLine4', language),
              t('ui.idleHintDayLine5', language)
            ]
            setIdleHintLines(rawLines.filter(line => line && !line.startsWith('ui.idleHint')))
          } else {
            setIdleHintTitle(t('ui.idleHintNightTitle', language))
            const rawLines = [
              t('ui.idleHintNightLine1', language),
              t('ui.idleHintNightLine2', language),
              t('ui.idleHintNightLine3', language),
              t('ui.idleHintNightLine4', language),
              t('ui.idleHintNightLine5', language)
            ]
            setIdleHintLines(rawLines.filter(line => line && !line.startsWith('ui.idleHint')))
          }
          setIdleHintVisible(true)
        }
      } else {
        setIdleHintVisible(false)
      }
    }, 500)
    return () => { window.clearInterval(intervalId) }
  }, [gameState?.phase, playerIsReady, localPlayerId, currentActivation, lastActionAt, language, isDismissed, isPaused, idleHintDismissCount, idleHintVisible])

  useEffect(() => {
    if (!gameState || !gameState.players) return
    const current = currentActivation
    if (!current) { if (reactTimerRef.current) { window.clearTimeout(reactTimerRef.current); reactTimerRef.current = null } return }
    // Use lastCardPlayerId as the source of truth for turn order
    const hasActiveActivation = gameState.activations.some(act => !act.resolved && !act.accepted);
    const isMyTurnToRespond = hasActiveActivation &&
      gameState.lastCardPlayerId !== undefined &&
      gameState.lastCardPlayerId !== localPlayerId;
    if (!isMyTurnToRespond) return
    if (reactTimerRef.current) window.clearTimeout(reactTimerRef.current)
    reactTimerRef.current = window.setTimeout(() => {
      const targetCard = current.counterChain.length > 0 ? current.counterChain[current.counterChain.length - 1] : current.attackingCard
      const type = targetCard.card.type
      const me = gameState.players.find(p => p.id === localPlayerId)!
      const counters = me.prepared.filter(pc => {
        const t = pc.card.type
        if (t === CardType.Dispel && (targetCard.card.subType === 'Spell' || targetCard.card.subType === 'Artifact')) return true
        return [CardType.Hibernation, CardType.Invincible, CardType.Pushback, CardType.Defense, CardType.PsyBlock, CardType.Counterblast].includes(t)
      })
      const icons = counters.slice(0, 6).map(pc => getTypeIconForType(pc.card.type))
      const lines: string[] = []
      const special: string[] = []
      const c = targetCard.card
      lines.push(getCardSummaryForType(type, (c as any).faceSlug, language))
      if (c.essenceCost) {
        const owner = gameState.players.find(p => p.id === c.ownerId) || gameState.players.find(p => p.id === localPlayerId)!
        const showDiscount = !!owner && owner.playerClass === 'OCCULTIST' && c.subType === 'Artifact'
        const costToShow = showDiscount ? getEffectiveEssenceCostForPlayer(c, owner) : c.essenceCost
        special.push(`${t('term.essence', language)} ${costToShow}`)
      }
      if (c.extendedShield) special.push(t('term.shieldReaction', language))
      if (c.playableAfterPass) special.push(t('term.playableAfterPass', language))
      if (special.length > 0) lines.push(special.join(' • '))
      setHintTitle(`${t('ui.opponent', language)}: ${getCardDisplayNameForCard(targetCard.card, targetCard.rotation, language)}`)
      setHintLines(lines)
      setHintIcons(icons)
      if (tooltipsEnabled) {
        hintShownAtRef.current = Date.now()
        setHintVisible(true)
      }
    }, 15000)
    return () => { if (reactTimerRef.current) { window.clearTimeout(reactTimerRef.current); reactTimerRef.current = null } }
  }, [currentActivation, gameState, localPlayerId, tooltipsEnabled])

  useEffect(() => {
    if (gameState?.seed) {
      setIdleHintDismissCount(0)
      localStorage.setItem('idleHintDismissCount', '0')
    }
  }, [gameState?.seed])

  return {
    hintVisible,
    hintTitle,
    hintLines,
    hintIcons,
    idleHintVisible,
    idleHintTitle,
    idleHintLines,
    handleCardMouseEnterApp,
    handleCardMouseLeaveApp,
    markAction,
    dismissIdleHint,
    handleCloseReactionHint,
    maskingDelay,
  }
}
